import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSnapshot } from 'mobx-state-tree';
import { cloneDeep } from 'lodash-es';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Chip,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { useDrag, useDrop } from 'react-dnd';
import type { IPortal, IAsset } from '@mcn-platform/models';
import { useStore } from '@mcn-platform/models';
import AssetPickerDialog from 'components/asset/AssetPickerDialog';
import {
  DEFAULT_WIZARD_STEPS,
  getFormattedTextPlain,
  isShoeSizeStep,
  getWizardsFromConfig,
  type WizardStep,
  type WizardQuestion,
  type WizardShoeSizeQuestion,
  type WizardOption,
  type SelectionWizardConfig,
} from '@mcn-platform/converse-lib';
import FormattedTextEditor from './FormattedTextEditor';

const DND_TYPE_WIZARD_STEP = 'wizardStep';

/** Merge wizards into portal.clientAIConfig using a plain snapshot (object spread on MST nodes drops nested updates). */
export const mergePortalClientAIConfigWithWizards = (
  portal: IPortal,
  wizards: SelectionWizardConfig[],
): Record<string, unknown> => {
  const prev = portal.clientAIConfig;
  const base = prev ? (cloneDeep(getSnapshot(prev)) as Record<string, unknown>) : {};
  return { ...base, wizards };
};

const assetRefForPersistence = (a: IAsset): string => {
  const k = (a as { key?: string }).key;
  if (typeof k === 'string' && k.length > 0) return k;
  return a.id;
};

/** Image picker with public uploads; persists asset key (`org~id`) and a resolved image URL in wizard config. */
const WizardImageAssetField = ({
  name,
  label,
  helperText,
  imageAssetKey,
  onResolvedChange,
}: {
  name: string;
  label: string;
  helperText?: string;
  imageAssetKey?: string;
  onResolvedChange: (next: { imageAssetKey?: string; imageUrl?: string }) => void;
}) => {
  const store = useStore();
  const [asset, setAsset] = useState<IAsset | undefined>();

  useEffect(() => {
    if (imageAssetKey) {
      setAsset(store.getOrLoad('asset', imageAssetKey));
    } else {
      setAsset(undefined);
    }
  }, [store, imageAssetKey]);

  const handleChange = async (newAsset: IAsset | null | undefined) => {
    if (!newAsset) {
      onResolvedChange({ imageAssetKey: undefined, imageUrl: undefined });
      setAsset(undefined);
      return;
    }
    setAsset(newAsset);
    const key = assetRefForPersistence(newAsset);
    let imageUrl: string | undefined;
    try {
      if (typeof newAsset.getSignedUrl === 'function') {
        const entry = await newAsset.getSignedUrl('original');
        const u = entry?.url;
        if (typeof u === 'string' && u.trim()) imageUrl = u.trim();
      }
    } catch {
      // Still persist key so runtime can resolve URL
    }
    onResolvedChange({ imageAssetKey: key, imageUrl });
  };

  return (
    <FormControl fullWidth sx={{ mt: 1 }}>
      <FormLabel sx={{ mb: 0.5 }}>{label}</FormLabel>
      <Box
        sx={[
          {
            width: '100%',
            border: '1px solid',
            borderColor: '#e0e0eb',
            borderRadius: 1,
            p: 2,
          },
          {
            '&:hover': {
              borderColor: '#111111',
            },
          },
        ]}
      >
        <AssetPickerDialog
          value={asset}
          name={name}
          canUpload
          assetType="image"
          onChange={handleChange}
          publicAccess
          tags=""
        />
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

WizardImageAssetField.defaultProps = {
  helperText: undefined,
  imageAssetKey: undefined,
};

/**
 * Keeps typing local and commits on blur/enter to avoid expensive parent-wide
 * re-renders on every keystroke in large wizard forms.
 */
const BufferedTextField = ({
  value,
  onCommit,
  ...rest
}: {
  value: string;
  onCommit: (next: string) => void;
} & React.ComponentProps<typeof TextField>) => {
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = useCallback(() => {
    if (draft !== (value ?? '')) onCommit(draft);
  }, [draft, value, onCommit]);

  return (
    <TextField
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
};

function moveStep(steps: WizardStep[], from: number, to: number): WizardStep[] {
  const copy = steps.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

const defaultQuestionStep = (): WizardQuestion => ({
  id: `q_${Date.now()}`,
  label: 'New question',
  selectType: 'single',
  options: [{ value: 'opt1', label: 'Option 1' }],
});

const defaultSizesList = (): string[] => ['7', '8', '9', '10', '11', '12'];
const defaultWidthList = (): WizardOption[] => [{ value: 'D', label: 'D (Medium)' }];

const defaultShoeSizeStep = (): WizardShoeSizeQuestion => ({
  id: 'shoe_size',
  label: 'Shoe size, width and gender',
  type: 'gender_size_width',
  genderOptions: [{ value: 'men', label: 'Men' }, { value: 'women', label: 'Women' }],
  sizeOptions: { men: defaultSizesList(), women: defaultSizesList() },
  widthOptions: {
    men: defaultWidthList(),
    women: defaultWidthList(),
  },
});

/** Rename gender `value` and migrate size/width band keys. No-op if duplicate or empty value. */
function renameGenderBandKey(
  step: WizardShoeSizeQuestion,
  index: number,
  newValue: string,
): WizardShoeSizeQuestion {
  const trimmed = newValue.trim();
  const opt = step.genderOptions[index];
  if (!opt || trimmed === opt.value) return step;
  if (!trimmed || step.genderOptions.some((g, i) => i !== index && g && g.value === trimmed)) {
    return step;
  }
  const old = opt.value;
  const nextOpts = step.genderOptions.slice();
  nextOpts[index] = { ...opt, value: trimmed };
  const so = { ...step.sizeOptions };
  const wo = { ...step.widthOptions };
  if (so[old] !== undefined) {
    so[trimmed] = so[old];
    delete so[old];
  }
  if (wo[old] !== undefined) {
    wo[trimmed] = wo[old];
    delete wo[old];
  }
  return { ...step, genderOptions: nextOpts, sizeOptions: so, widthOptions: wo };
}

// Option row for question options (value, label, optional description, optional image asset)
const OptionRow = ({
  opt,
  onChange,
  onRemove,
  canRemove,
}: {
  opt: WizardOption;
  onChange: (o: WizardOption) => void;
  onRemove: () => void;
  canRemove: boolean;
}) => (
  <Stack spacing={1} sx={{ mb: 2 }}>
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <BufferedTextField
        size="small"
        label="Value"
        value={opt.value}
        onCommit={(next) => onChange({ ...opt, value: next })}
        sx={{ minWidth: 140 }}
      />
      <BufferedTextField
        size="small"
        label="Label"
        value={opt.label}
        onCommit={(next) => onChange({ ...opt, label: next })}
        sx={{ flex: 1 }}
      />
      <BufferedTextField
        size="small"
        label="Description (optional)"
        value={opt.description ?? ''}
        onCommit={(next) => onChange({ ...opt, description: next || undefined })}
        sx={{ flex: 1 }}
      />
      {canRemove && (
        <IconButton size="small" onClick={onRemove} aria-label="Remove option">
          <Delete />
        </IconButton>
      )}
    </Stack>
    <WizardImageAssetField
      name={`wizard-opt-${opt.value}-image`}
      label="Option image (optional)"
      helperText="Shown on the right when selected; overrides question image. Uploads are public."
      imageAssetKey={opt.imageAssetKey}
      onResolvedChange={(next) => onChange({ ...opt, ...next })}
    />
  </Stack>
);

// Inline editor for a single question step
const QuestionStepEditor = ({
  step,
  onChange,
}: {
  step: WizardQuestion;
  onChange: (s: WizardQuestion) => void;
}) => (
  <Stack spacing={2}>
    <BufferedTextField
      size="small"
      label="Question ID"
      value={step.id}
      onCommit={(next) => onChange({ ...step, id: next })}
      fullWidth
    />
    <FormattedTextEditor
      value={step.label}
      onChange={(next) => onChange({ ...step, label: next })}
    />
    <WizardImageAssetField
      name={`wizard-q-${step.id}-image`}
      label="Question image (optional)"
      helperText="Public uploads (Suggested Prompt style). Right image until an option image applies."
      imageAssetKey={step.imageAssetKey}
      onResolvedChange={(next) => onChange({ ...step, ...next })}
    />
    <TextField select size="small" label="Select type" value={step.selectType ?? 'single'} onChange={(e) => onChange({ ...step, selectType: e.target.value as 'single' | 'multiple' })} sx={{ minWidth: 120 }}>
      <MenuItem value="single">Single choice</MenuItem>
      <MenuItem value="multiple">Multiple choice</MenuItem>
    </TextField>
    <Typography variant="subtitle2">Options</Typography>
    {(step.options || []).map((opt, idx) => opt && typeof opt === 'object' && !Array.isArray(opt) ? (
      <OptionRow
        key={idx}
        opt={opt}
        onChange={(o) => {
          const next = (step.options || []).slice();
          next[idx] = o;
          onChange({ ...step, options: next });
        }}
        onRemove={() => onChange({ ...step, options: (step.options || []).filter((_, i) => i !== idx) })}
        canRemove={(step.options || []).length > 1}
      />
    ) : null)}
    <Button size="small" startIcon={<Add />} onClick={() => onChange({ ...step, options: [...(step.options || []), { value: `opt_${Date.now()}`, label: 'New option' }] })}>
      Add option
    </Button>
  </Stack>
);

// Inline editor for shoe size step: gender options + per-gender sizes + per-gender width rows
const ShoeSizeStepEditor = ({
  step,
  onChange,
}: {
  step: WizardShoeSizeQuestion;
  onChange: (s: WizardShoeSizeQuestion) => void;
}) => {
  const addGender = useCallback(() => {
    const newVal = `gender_${Date.now()}`;
    onChange({
      ...step,
      genderOptions: [...(step.genderOptions || []), { value: newVal, label: 'New gender' }],
      sizeOptions: { ...step.sizeOptions, [newVal]: defaultSizesList() },
      widthOptions: { ...step.widthOptions, [newVal]: defaultWidthList() },
    });
  }, [step, onChange]);

  const removeGender = useCallback(
    (idx: number) => {
      if ((step.genderOptions || []).length <= 1) return;
      const g = step.genderOptions[idx];
      const nextG = (step.genderOptions || []).filter((_, i) => i !== idx);
      const so = { ...step.sizeOptions };
      const wo = { ...step.widthOptions };
      delete so[g.value];
      delete wo[g.value];
      onChange({ ...step, genderOptions: nextG, sizeOptions: so, widthOptions: wo });
    },
    [step, onChange],
  );

  const addWidthRow = useCallback(
    (genderKey: string) => {
      const prev = step.widthOptions[genderKey] ?? defaultWidthList();
      const nextList = [...prev, { value: `w_${Date.now()}`, label: 'New width' }];
      onChange({ ...step, widthOptions: { ...step.widthOptions, [genderKey]: nextList } });
    },
    [step, onChange],
  );

  const removeWidthRow = useCallback(
    (genderKey: string, rowIdx: number) => {
      const prev = step.widthOptions[genderKey] ?? [];
      if (prev.length <= 1) return;
      const nextList = prev.filter((_, i) => i !== rowIdx);
      onChange({ ...step, widthOptions: { ...step.widthOptions, [genderKey]: nextList } });
    },
    [step, onChange],
  );

  return (
    <Stack spacing={2}>
      <BufferedTextField
        size="small"
        label="Step ID"
        value={step.id}
        onCommit={(next) => onChange({ ...step, id: next })}
        fullWidth
      />
      <FormattedTextEditor
        value={step.label}
        onChange={(next) => onChange({ ...step, label: next })}
      />
      <WizardImageAssetField
        name={`wizard-shoe-${step.id}-image`}
        label="Question image (optional)"
        helperText="Shown beside this step. Gender, size, and width chips do not use images."
        imageAssetKey={step.imageAssetKey}
        onResolvedChange={(next) => onChange({ ...step, ...next })}
      />
      <Typography variant="subtitle2">Gender options</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
        Value is the internal key for size and width bands (change it here, then tab out, to rename safely).
      </Typography>
      {(step.genderOptions || []).map((opt, idx) => opt && typeof opt === 'object' && !Array.isArray(opt) ? (
        <Stack key={`${idx}-${opt.value}`} direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            label="Value"
            defaultValue={opt.value}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== opt.value) {
                const next = renameGenderBandKey(step, idx, v);
                if (next !== step) onChange(next);
              }
            }}
            sx={{ width: 140 }}
          />
          <BufferedTextField
            size="small"
            label="Label"
            value={opt.label}
            onCommit={(nextLabel) => {
              const next = step.genderOptions.slice();
              next[idx] = { ...next[idx], label: nextLabel };
              onChange({ ...step, genderOptions: next });
            }}
            sx={{ flex: 1 }}
          />
          {(step.genderOptions || []).length > 1 && (
            <IconButton size="small" onClick={() => removeGender(idx)} aria-label="Remove gender option">
              <Delete />
            </IconButton>
          )}
        </Stack>
      ) : null)}
      <Button size="small" startIcon={<Add />} onClick={addGender}>
        Add gender option
      </Button>
      <Typography variant="subtitle2">Size options (comma-separated per gender)</Typography>
      {(step.genderOptions || []).map((g) => g && typeof g === 'object' && !Array.isArray(g) ? (
        <BufferedTextField
          key={g.value}
          size="small"
          label={`Sizes — ${g.label}`}
          value={((step.sizeOptions || {})[g.value] ?? []).join(', ')}
          onCommit={(nextValue) =>
            onChange({
              ...step,
              sizeOptions: {
                ...step.sizeOptions,
                [g.value]: nextValue.split(/,\s*/).filter(Boolean),
              },
            })
          }
          fullWidth
        />
      ) : null)}
      <Typography variant="subtitle2">Width options (per gender)</Typography>
      {(step.genderOptions || []).map((g) => {
        if (!g || typeof g !== 'object' || Array.isArray(g)) return null;
        const rows = (step.widthOptions || {})[g.value] ?? defaultWidthList();
        return (
          <Box key={g.value} sx={{ pl: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {g.label}
              {' '}
              (
              {g.value}
              )
            </Typography>
            {rows.map((opt, idx) => opt && typeof opt === 'object' && !Array.isArray(opt) ? (
              <Stack key={idx} direction="row" spacing={1} sx={{ mb: 0.5 }} alignItems="flex-start">
                <BufferedTextField
                  size="small"
                  placeholder="Value"
                  value={opt.value}
                  onCommit={(nextValue) => {
                    const next = rows.slice();
                    next[idx] = { ...next[idx], value: nextValue };
                    onChange({ ...step, widthOptions: { ...step.widthOptions, [g.value]: next } });
                  }}
                  sx={{ width: 100 }}
                />
                <BufferedTextField
                  size="small"
                  placeholder="Label"
                  value={opt.label}
                  onCommit={(nextLabel) => {
                    const next = rows.slice();
                    next[idx] = { ...next[idx], label: nextLabel };
                    onChange({ ...step, widthOptions: { ...step.widthOptions, [g.value]: next } });
                  }}
                  sx={{ flex: 1 }}
                />
                {rows.length > 1 && (
                  <IconButton size="small" onClick={() => removeWidthRow(g.value, idx)} aria-label="Remove width option">
                    <Delete />
                  </IconButton>
                )}
              </Stack>
            ) : null)}
            <Button size="small" startIcon={<Add />} onClick={() => addWidthRow(g.value)} sx={{ mt: 0.5 }}>
              Add width option
            </Button>
          </Box>
        );
      })}
    </Stack>
  );
};

// Draggable step row
const StepRow = ({
  step,
  index,
  onUpdate,
  onRemove,
  onMove,
}: {
  step: WizardStep;
  index: number;
  onUpdate: (s: WizardStep) => void;
  onRemove: () => void;
  onMove: (from: number, to: number) => void;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE_WIZARD_STEP,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [, drop] = useDrop({
    accept: DND_TYPE_WIZARD_STEP,
    hover: (item: { index: number }, monitor) => {
      if (!ref.current || item.index === index) return;
      const hoverRect = ref.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverMiddleY = (hoverRect.top + hoverRect.bottom) / 2;
      if (item.index < index && clientOffset.y < hoverMiddleY) return;
      if (item.index > index && clientOffset.y > hoverMiddleY) return;
      onMove(item.index, index);
      (item as { index: number }).index = index;
    },
  });
  drag(drop(ref));
  const isShoe = isShoeSizeStep(step);
  const labelText = getFormattedTextPlain(step.label);
  const summaryLabel = isShoe ? labelText : `${(step as WizardQuestion).selectType === 'multiple' ? 'Multi' : 'Single'}: ${labelText}`;

  return (
    <Accordion sx={{ mb: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ opacity: isDragging ? 0.6 : 1 }}>
        <Stack ref={ref} direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <DragIndicator sx={{ color: 'action.disabled', cursor: 'grab' }} />
          <Chip size="small" label={isShoe ? 'Shoe size' : 'Question'} color={isShoe ? 'secondary' : 'default'} variant="outlined" />
          <Typography variant="body2" sx={{ flex: 1 }} noWrap>{summaryLabel}</Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label="Remove step">
            <Delete />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {isShoe ? (
          <ShoeSizeStepEditor step={step} onChange={(s) => onUpdate(s)} />
        ) : (
          <QuestionStepEditor step={step as WizardQuestion} onChange={(s) => onUpdate(s)} />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export type WizardToolQuestionsFieldProps = { portal: IPortal };

export const defaultNewWizard = (): SelectionWizardConfig => ({
  id: `wizard_${Date.now()}`,
  name: 'New wizard',
  steps: [...DEFAULT_WIZARD_STEPS],
});

/** Single-wizard editor for use in a modal (controlled: wizard + onChange). */
export const WizardEditor: React.FC<{
  wizard: SelectionWizardConfig;
  onChange: (w: SelectionWizardConfig) => void;
}> = ({ wizard, onChange }) => {
  const updateStep = useCallback((stepIndex: number, next: WizardStep) => {
    const steps = (wizard.steps || []).slice();
    steps[stepIndex] = next;
    onChange({ ...wizard, steps });
  }, [wizard, onChange]);
  const removeStep = useCallback((stepIndex: number) => {
    onChange({ ...wizard, steps: (wizard.steps || []).filter((_, i) => i !== stepIndex) });
  }, [wizard, onChange]);
  const moveStepAt = useCallback((from: number, to: number) => {
    onChange({ ...wizard, steps: moveStep(wizard.steps || [], from, to) });
  }, [wizard, onChange]);
  const addQuestion = useCallback(() => {
    onChange({ ...wizard, steps: [...(wizard.steps || []), defaultQuestionStep()] });
  }, [wizard, onChange]);
  const addShoeSize = useCallback(() => {
    onChange({ ...wizard, steps: [...(wizard.steps || []), defaultShoeSizeStep()] });
  }, [wizard, onChange]);
  const resetWizardToDefault = useCallback(() => {
    onChange({ ...wizard, steps: [...DEFAULT_WIZARD_STEPS] });
  }, [wizard, onChange]);

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Wizard ID"
          value={wizard.id ?? ''}
          onChange={(e) => onChange({ ...wizard, id: e.target.value })}
          sx={{ width: 220 }}
          helperText="Used by the AI to pick this wizard (e.g. shoes, runnersWatch)"
        />
        <TextField
          size="small"
          label="Display name"
          value={wizard.name ?? ''}
          onChange={(e) => onChange({ ...wizard, name: e.target.value || undefined })}
          sx={{ width: 220 }}
          placeholder="e.g. Shoes"
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={addQuestion}>
          Add question
        </Button>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={addShoeSize}>
          Add shoe size step
        </Button>
        <Button size="small" onClick={resetWizardToDefault}>
          Reset to default
        </Button>
      </Stack>
      {(wizard.steps || []).map((step, stepIndex) => step && typeof step === 'object' && !Array.isArray(step) ? (
        <StepRow
          key={`${wizard.id}-${step.id}-${stepIndex}`}
          step={step}
          index={stepIndex}
          onUpdate={(s) => updateStep(stepIndex, s)}
          onRemove={() => removeStep(stepIndex)}
          onMove={(from, to) => moveStepAt(from, to)}
        />
      ) : null)}
    </Stack>
  );
};

const DEBOUNCE_MS = 400;

const WizardToolQuestionsField: React.FC<WizardToolQuestionsFieldProps> = ({ portal }) => {
  const [wizards, setWizards] = useState<SelectionWizardConfig[]>(() =>
    getWizardsFromConfig(portal?.clientAIConfig ?? undefined),
  );
  const didInitialSync = useRef(false);
  const lastWizardsRef = useRef(wizards);
  const lastPortalRef = useRef(portal);
  lastWizardsRef.current = wizards;
  lastPortalRef.current = portal;

  useEffect(() => {
    setWizards(getWizardsFromConfig(portal?.clientAIConfig ?? undefined));
    didInitialSync.current = false;
  }, [portal?.id, portal?.syncState]);

  useEffect(() => {
    if (!portal) return;
    if (!didInitialSync.current) {
      didInitialSync.current = true;
      return;
    }
    const id = setTimeout(() => {
      // Only write back if wizards actually differ from portal (avoids marking form dirty on load)
      const fromPortal = getWizardsFromConfig(portal.clientAIConfig ?? undefined);
      if (JSON.stringify(wizards) === JSON.stringify(fromPortal)) return;
      const config = mergePortalClientAIConfigWithWizards(portal, wizards);
      portal.updateFields(['clientAIConfig'], { clientAIConfig: config } as never);
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [wizards, portal]);

  // Flush pending update on unmount
  useEffect(
    () => () => {
      if (lastPortalRef.current && didInitialSync.current) {
        const config = mergePortalClientAIConfigWithWizards(
          lastPortalRef.current,
          lastWizardsRef.current,
        );
        lastPortalRef.current.updateFields(['clientAIConfig'], {
          clientAIConfig: config,
        } as never);
      }
    },
    [],
  );

  const updateWizard = useCallback((wizardIndex: number, updater: (w: SelectionWizardConfig) => SelectionWizardConfig) => {
    setWizards((prev) => {
      const copy = prev.slice();
      copy[wizardIndex] = updater(copy[wizardIndex]);
      return copy;
    });
  }, []);

  const updateStep = useCallback((wizardIndex: number, stepIndex: number, next: WizardStep) => {
    updateWizard(wizardIndex, (w) => {
      const steps = w.steps.slice();
      steps[stepIndex] = next;
      return { ...w, steps };
    });
  }, [updateWizard]);

  const removeStep = useCallback((wizardIndex: number, stepIndex: number) => {
    updateWizard(wizardIndex, (w) => ({
      ...w,
      steps: w.steps.filter((_, i) => i !== stepIndex),
    }));
  }, [updateWizard]);

  const moveStepAt = useCallback((wizardIndex: number, from: number, to: number) => {
    updateWizard(wizardIndex, (w) => ({ ...w, steps: moveStep(w.steps, from, to) }));
  }, [updateWizard]);

  const addQuestion = useCallback((wizardIndex: number) => {
    updateWizard(wizardIndex, (w) => ({ ...w, steps: [...w.steps, defaultQuestionStep()] }));
  }, [updateWizard]);

  const addShoeSize = useCallback((wizardIndex: number) => {
    updateWizard(wizardIndex, (w) => ({ ...w, steps: [...w.steps, defaultShoeSizeStep()] }));
  }, [updateWizard]);

  const resetWizardToDefault = useCallback((wizardIndex: number) => {
    updateWizard(wizardIndex, (w) => ({ ...w, steps: [...DEFAULT_WIZARD_STEPS] }));
  }, [updateWizard]);

  const addWizard = useCallback(() => {
    setWizards((prev) => [...prev, defaultNewWizard()]);
  }, []);

  const removeWizard = useCallback((wizardIndex: number) => {
    setWizards((prev) => prev.filter((_, i) => i !== wizardIndex));
  }, []);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Wizard questions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure one or more preference wizards (e.g. shoes, runner&apos;s watch). The AI will pick the correct wizard from the topic. Reorder steps by dragging. Changes are saved when you click Save on this page.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={addWizard}>
          Add wizard
        </Button>
      </Stack>
      {wizards.map((wizard, wizardIndex) => (
        <Accordion key={wizardIndex} defaultExpanded sx={{ mb: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {wizard.name || wizard.id || 'New wizard'}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); removeWizard(wizardIndex); }}
                aria-label="Remove wizard"
              >
                <Delete />
              </IconButton>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap">
                <TextField
                  size="small"
                  label="Wizard ID"
                  value={wizard.id}
                  onChange={(e) => updateWizard(wizardIndex, (w) => ({ ...w, id: e.target.value }))}
                  sx={{ width: 220 }}
                  helperText="Used by the AI to pick this wizard (e.g. shoes, runnersWatch)"
                />
                <TextField
                  size="small"
                  label="Display name"
                  value={wizard.name ?? ''}
                  onChange={(e) => updateWizard(wizardIndex, (w) => ({ ...w, name: e.target.value || undefined }))}
                  sx={{ width: 220 }}
                  placeholder="e.g. Shoes"
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addQuestion(wizardIndex)}>
                Add question
              </Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addShoeSize(wizardIndex)}>
                Add shoe size step
              </Button>
              <Button size="small" onClick={() => resetWizardToDefault(wizardIndex)}>
                Reset to default
              </Button>
            </Stack>
            {wizard.steps.map((step, stepIndex) => (
              <StepRow
                key={`${wizard.id}-${step.id}-${stepIndex}`}
                step={step}
                index={stepIndex}
                onUpdate={(s) => updateStep(wizardIndex, stepIndex, s)}
                onRemove={() => removeStep(wizardIndex, stepIndex)}
                onMove={(from, to) => moveStepAt(wizardIndex, from, to)}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default WizardToolQuestionsField;
